import React, { useRef, useEffect } from 'react'
import { repairAndInquiriesApi } from '../../../../../../api/repairAndInquiries'

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 8 12 3 7 8" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="3" x2="12" y2="15" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const UploadSmallIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export interface AttachmentEntry {
  file: File
  imageReference: string
  analyzing: boolean
  analyzedWithCategory?: string
  analyzedWithDescription?: string
}

interface AttachmentsStepProps {
  files: AttachmentEntry[]
  category: string
  description: string
  onChange: (files: AttachmentEntry[]) => void
}

const AttachmentsStep = ({ files, category, description, onChange }: AttachmentsStepProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const filesRef = useRef(files)
  useEffect(() => { filesRef.current = files }, [files])

  const analyzeEntry = (entry: AttachmentEntry) => {
    if (!entry.file.type.startsWith('image/')) return
    onChange(filesRef.current.map((f) =>
      f.imageReference === entry.imageReference ? { ...f, analyzing: true } : f
    ))
    repairAndInquiriesApi.analyzeImage(entry.file, category, entry.imageReference)
      .catch(() => {})
      .finally(() => {
        onChange(filesRef.current.map((f) =>
          f.imageReference === entry.imageReference
            ? { ...f, analyzing: false, analyzedWithCategory: category, analyzedWithDescription: description }
            : f
        ))
      })
  }

  // Re-analyze images whose category or description has changed since last analysis
  useEffect(() => {
    const stale = filesRef.current.filter(
      (f) =>
        !f.analyzing &&
        f.file.type.startsWith('image/') &&
        (f.analyzedWithCategory !== category || f.analyzedWithDescription !== description)
    )
    stale.forEach(analyzeEntry)
  }, []) // runs on every mount (step re-enter)

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return
    const added: AttachmentEntry[] = Array.from(newFiles).map((file) => ({
      file,
      imageReference: crypto.randomUUID(),
      analyzing: true,
    }))

    const next = [...filesRef.current, ...added]
    onChange(next)

    added.forEach((entry) => {
      repairAndInquiriesApi.analyzeImage(entry.file, category, entry.imageReference)
        .catch(() => {})
        .finally(() => {
          onChange(filesRef.current.map((f) =>
            f.imageReference === entry.imageReference
              ? { ...f, analyzing: false, analyzedWithCategory: category, analyzedWithDescription: description }
              : f
          ))
        })
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0a0d47', margin: '0 0 4px' }}>Add photos or a video</h3>
        <p style={{ fontSize: '0.875rem', color: '#9ca3af', margin: 0 }}>Upload images or a short video to help us better understand the damage.</p>
      </div>

      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '1.5px dashed #c7d2fe',
          borderRadius: '12px',
          padding: '48px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          backgroundColor: '#fff',
        }}
      >
        <UploadIcon />
        <span style={{ fontSize: '0.9375rem', color: '#374151', fontWeight: 500 }}>Click to upload files</span>
        <span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>JPG, PNG, PDF, MP4 — up to 25 MB each</span>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}
        >
          <UploadSmallIcon /> Upload from device
        </button>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}
        >
          <CameraIcon /> Take a photo
        </button>
      </div>

      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {files.map((entry, i) => (
            <div key={entry.imageReference} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8125rem', color: '#374151', padding: '6px 10px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {entry.file.name}
                {entry.analyzing && (
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Analysing…</span>
                )}
              </span>
              <button type="button" onClick={() => {
                repairAndInquiriesApi.deleteImageAnalysis(entry.imageReference).catch(() => {/* graceful — TTL will clean up */})
                onChange(files.filter((_, fi) => fi !== i))
              }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}

      <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf,.mp4" multiple style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files)} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files)} />
    </div>
  )
}

export default AttachmentsStep
